import React, { useState, useEffect, useRef } from 'react';
import { Formik, Form as FormMik } from 'formik';
import { Table, Form, Button } from 'react-bootstrap';
import * as Yup from 'yup';
import { cep, cnpj } from '../components/masks/masks';
import { useToast } from '../hooks/toast';

import api from '../services/apiClient';

interface Empresa {
  seqEmpresa: number;
  nome: string;
  endereco: string;
  bairro: string;
  cidade: string;
  cep: string;
  cnpj: string;
  telefone: string;
  email: string;
  linkEmpresa: string;
  ativo: boolean;
}

const Company: React.FC = () => {
  const { addToast } = useToast();
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [exibeFormulario, setExibeFormulario] = useState(false);
  const [dataLoad, setDataLoad] = useState<Empresa>({} as Empresa);

  const bntCadastrar = useRef<HTMLButtonElement>(null);

  const token = localStorage.getItem('@LiveChat:token');

  const initialValues = {
    seqEmpresa: 0,
    nome: '',
    endereco: '',
    bairro: '',
    cidade: '',
    cep: '',
    cnpj: '',
    telefone: '',
    email: '',
    linkEmpresa: '',
    ativo: true,
  };

  const loadEmpresas = (): void => {
    api
      .get('/Empresa', { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => {
        const result = response.data;

        if (result.success) {
          for (let row = 0; row < result.data.length; row += 1) {
            result.data[row].ativo = result.data[row].ativo === 'S';
          }

          setEmpresas(result.data);
        }
      });
  };

  const loadEmpresa = (seqEmpresa: number): void => {
    api
      .get(`/empresa/${seqEmpresa}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        const result = response.data;

        if (result.success) {
          result.data.ativo = result.data.ativo === 'S';

          setDataLoad(result.data);
          if (bntCadastrar && bntCadastrar.current) {
            bntCadastrar.current.innerText = 'Atualizar';
          }
        } else {
          addToast({
            type: 'error',
            title: 'Erro',
            description: result.message,
          });
        }
      });
  };

  useEffect(() => {
    loadEmpresas();
    setDataLoad(initialValues);
  }, []);

  const validationSchemaEmpresa = Yup.object().shape({
    nome: Yup.string()
      .required('Nome da empresa é obrigatório.')
      .max(60, 'Nome da empresa deve ter no máximo 60 caracteres'),
    endereco: Yup.string()
      .required('Endereço da empresa é obrigatório')
      .max(60, 'Endereço da empresa deve ter no máximo 60 caracteres'),
    bairro: Yup.string()
      .required('Bairro da empresa é obrigatório')
      .max(30, 'Bairro da empresa deve ter no máximo 30 caracteres'),
    cep: Yup.string()
      .required('CEP da empresa é obrigatório')
      .min(8, 'CEP da empresa deve ter 9 caracteres')
      .max(9, 'CEP da empresa deve ter 9 caracteres'),
    cnpj: Yup.string()
      .required('CNPJ da empresa é obrigatório')
      .min(14, 'CNPJ da empresa deve ter 15 caracteres')
      .max(18, 'CNPJ da empresa deve ter 15 caracteres'),
    telefone: Yup.string()
      .required('Telefone da empresa é obrigatório')
      .max(20, 'Telefone da empresa deve ter no máximo 20 caracteres'),
    email: Yup.string()
      .email('E-mail inválido')
      .required('E-mail da empresa é obrigatório')
      .max(100, 'E-mail da empresa deve ter no máximo 100 caracteres'),
    linkEmpresa: Yup.string()
      .required('Link da empresa é obrigatório')
      .max(100, 'Link da empresa deve ter no máximo 100 caracteres'),
    cidade: Yup.string()
      .required('Cidade da empresa é obrigatório')
      .max(60, 'Cidade da empresa deve ter no máximo 60 caracteres'),
  });

  const hundleSubmit = (values: Empresa): void => {
    const data = {
      seqEmpresa: values.seqEmpresa,
      nome: values.nome,
      endereco: values.endereco,
      bairro: values.bairro,
      cidade: values.cidade,
      cep: values.cep.replace('-', ''),
      cnpj: values.cnpj.replaceAll('.', '').replace('/', '').replace('-', ''),
      telefone: values.telefone,
      email: values.email,
      linkEmpresa: values.linkEmpresa,
      ativo: values.ativo ? 'S' : 'N',
    };

    if (dataLoad.seqEmpresa <= 0) {
      api
        .post('/empresa', data, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          const result = response.data;
          if (result.success) {
            loadEmpresas();
            addToast({ title: 'Cadastrado com sucesso', type: 'success' });
            setDataLoad(initialValues);
            setExibeFormulario(false);
          } else {
            const checkValues = values;
            setDataLoad(checkValues);
            addToast({ title: result.message, type: 'error' });
          }
        })
        .catch((error) => {
          console.log('error', error);
        });
    } else {
      api
        .put('/empresa', data, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          const result = response.data;
          if (result.success) {
            loadEmpresas();
            addToast({ title: 'Atualizado com sucesso', type: 'success' });
            setExibeFormulario(false);
          }
        })
        .catch((error) => {
          console.log('error', error);
        });
    }
  };

  return (
    <div className="ms-2 mb-2 h-100 overflow-auto">
      <h3 className="fw-bold">Cadastro de Empresa</h3>
      <Button
        variant="primary"
        size="sm"
        className="fw-bold mb-2"
        onClick={() => {
          setDataLoad(initialValues);
          setExibeFormulario(true);

          if (bntCadastrar && bntCadastrar.current) {
            bntCadastrar.current.innerText = 'Cadastrar';
          }
        }}
      >
        Nova Empresa
      </Button>

      {!exibeFormulario && (
        <section>
          <div className="d-flex me-2">
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Nome Empresa</th>
                  <th>Status</th>
                  <th>Opções</th>
                </tr>
              </thead>
              <tbody>
                {empresas.map((empresa) => (
                  <tr key={empresa.seqEmpresa}>
                    <td>{empresa.nome}</td>
                    <td>{empresa.ativo ? 'Ativo' : 'Cancelado'}</td>
                    <td>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => {
                          loadEmpresa(empresa.seqEmpresa);
                          setExibeFormulario(true);
                        }}
                      >
                        Editar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </section>
      )}

      {exibeFormulario && (
        <section className="m-2 ms-0 w-75">
          <Formik
            initialValues={dataLoad || initialValues}
            validationSchema={validationSchemaEmpresa}
            enableReinitialize
            onSubmit={(values, { setSubmitting, resetForm }) => {
              setSubmitting(true);
              hundleSubmit(values);
              resetForm();
              setSubmitting(false);
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              isSubmitting,
              resetForm,
            }) => (
              <FormMik>
                <Form.Group controlId="nome" className="mb-2">
                  <Form.Label>Nome da Empresa</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Nome da Empresa"
                    name="nome"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.nome}
                    className={`${
                      touched.nome && errors.nome ? 'is-invalid' : ''
                    }`}
                  />
                  {touched.nome && errors.nome ? (
                    <div className="invalid-feedback">{errors.nome}</div>
                  ) : null}
                </Form.Group>

                <Form.Group controlId="endereco" className="mb-2">
                  <Form.Label>Endereço</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Endereco da Empresa"
                    name="endereco"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.endereco}
                    className={`${
                      touched.endereco && errors.endereco ? 'is-invalid' : ''
                    }`}
                  />
                  {touched.endereco && errors.endereco ? (
                    <div className="invalid-feedback">{errors.endereco}</div>
                  ) : null}
                </Form.Group>

                <Form.Group controlId="bairro" className="mb-2">
                  <Form.Label>Bairro</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Bairro da Empresa"
                    name="bairro"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.bairro}
                    className={`${
                      touched.bairro && errors.bairro ? 'is-invalid' : ''
                    }`}
                  />
                  {touched.bairro && errors.bairro ? (
                    <div className="invalid-feedback">{errors.bairro}</div>
                  ) : null}
                </Form.Group>

                <Form.Group controlId="cidade" className="mb-2">
                  <Form.Label>Cidade</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Cidade da Empresa"
                    name="cidade"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.cidade}
                    className={`${
                      touched.cidade && errors.cidade ? 'is-invalid' : ''
                    }`}
                  />
                  {touched.cidade && errors.cidade ? (
                    <div className="invalid-feedback">{errors.cidade}</div>
                  ) : null}
                </Form.Group>

                <Form.Group controlId="cep" className="mb-2">
                  <Form.Label>Cep</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Cep da Empresa"
                    name="cep"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onKeyUp={(e) => cep(e)}
                    onFocus={(e) => cep(e)}
                    value={values.cep}
                    className={`${
                      touched.cep && errors.cep ? 'is-invalid' : ''
                    }`}
                  />
                  {touched.cep && errors.cep ? (
                    <div className="invalid-feedback">{errors.cep}</div>
                  ) : null}
                </Form.Group>

                <Form.Group controlId="cnpj" className="mb-2">
                  <Form.Label>CNPJ</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="CNPJ da Empresa"
                    name="cnpj"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.cnpj}
                    onKeyUp={(e) => cnpj(e)}
                    onFocus={(e) => cnpj(e)}
                    className={`${
                      touched.cnpj && errors.cnpj ? 'is-invalid' : ''
                    }`}
                  />
                  {touched.cnpj && errors.cnpj ? (
                    <div className="invalid-feedback">{errors.cnpj}</div>
                  ) : null}
                </Form.Group>

                <Form.Group controlId="telefone" className="mb-2">
                  <Form.Label>Telefone</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Telefone da Empresa"
                    name="telefone"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.telefone}
                    className={`${
                      touched.telefone && errors.telefone ? 'is-invalid' : ''
                    }`}
                  />
                  {touched.telefone && errors.telefone ? (
                    <div className="invalid-feedback">{errors.telefone}</div>
                  ) : null}
                </Form.Group>

                <Form.Group controlId="email" className="mb-2">
                  <Form.Label>E-mail</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Email da Empresa"
                    name="email"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.email}
                    className={` ${
                      touched.email && errors.email ? 'is-invalid' : ''
                    }`}
                  />
                  {touched.email && errors.email ? (
                    <div className="invalid-feedback">{errors.email}</div>
                  ) : null}
                </Form.Group>

                <Form.Group controlId="linkEmpresa" className="mb-2">
                  <Form.Label>Link Empresa</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Link da Empresa"
                    name="linkEmpresa"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.linkEmpresa}
                    className={`text-uppercase ${
                      touched.linkEmpresa && errors.linkEmpresa
                        ? 'is-invalid'
                        : ''
                    }`}
                  />
                  {touched.linkEmpresa && errors.linkEmpresa ? (
                    <div className="invalid-feedback">{errors.linkEmpresa}</div>
                  ) : null}
                </Form.Group>

                <Form.Check
                  type="checkbox"
                  name="ativo"
                  label="Ativo"
                  checked={values.ativo}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />

                <Button
                  variant="primary"
                  type="submit"
                  className="mt-2"
                  ref={bntCadastrar}
                  disabled={isSubmitting}
                >
                  Cadastrar
                </Button>

                <Button
                  variant="secondary"
                  type="button"
                  className="mt-2 ms-3"
                  disabled={isSubmitting}
                  onClick={() => {
                    setDataLoad(initialValues);
                    resetForm();
                    setExibeFormulario(false);
                    if (bntCadastrar && bntCadastrar.current) {
                      bntCadastrar.current.innerText = 'Atualizar';
                    }
                  }}
                >
                  Cancelar Cadastro
                </Button>
              </FormMik>
            )}
          </Formik>
        </section>
      )}
    </div>
  );
};

export default Company;
